/*
 * VENUE ZONES AS THE SERVER STATES THEM (decision 785).
 *
 * `Intl.DateTimeFormat({ timeZone: "Africa/Casablanca" })` reads the zone
 * through the BROWSER's own copy of the tz database. Safari uses the operating
 * system's; Firefox ships its own and updates it only with a Firefox release.
 * In October 2026 Firefox on macOS still had Morocco at +01 a month after it
 * moved to +00, so every Casablanca time on Active read an hour late there and
 * right on iOS — the same failure decision 780 removed from Postgres, one layer
 * out.
 *
 * So the server sends every venue zone's clock as spans of fixed offset
 * (`/api/me`'s `zones`, computed from the image's vendored tzdata), and a venue
 * instant is formatted by shifting it by the span's offset and formatting the
 * result AS UTC. UTC needs no zone data, so the browser's copy is never asked
 * about a venue. A zone the server did not send falls back to the browser, which
 * is how every zone was formatted before.
 *
 * The DEVICE's zone is still the browser's: it is the reader's own clock, and
 * the device is the authority on it.
 */

export interface ZoneSpan {
  /** From this instant until the next span, the zone reads UTC + offset_minutes. */
  from: string;
  offset_minutes: number;
  /** The tz database's abbreviation, e.g. "CDT" or "+01". */
  abbrev: string;
}

interface Parsed {
  from: number;
  offset: number;
  abbrev: string;
}

const table = new Map<string, Parsed[]>();

function parse(spans: readonly ZoneSpan[]): Parsed[] {
  return spans
    .map((s) => ({ from: Date.parse(s.from), offset: s.offset_minutes, abbrev: s.abbrev }))
    .filter((s) => !Number.isNaN(s.from))
    .sort((a, b) => a.from - b.from);
}

/** Replace the known zones with the server's (every `/api/me`). */
export function setZoneSpans(
  zones: Record<string, readonly ZoneSpan[] | null> | null | undefined,
): void {
  table.clear();
  for (const [zone, spans] of Object.entries(zones ?? {})) {
    if (spans) table.set(zone, parse(spans));
  }
}

export interface VenueClock {
  /** Minutes east of UTC at the instant. */
  offset: number;
  abbrev: string;
}

/**
 * The venue zone's offset at an instant, from the server's spans: those passed
 * in (the service worker, which has no `/api/me`), else the table. Null when
 * neither covers the instant, which sends the caller back to the browser.
 */
export function venueClock(
  zone: string,
  instant: Date,
  spans?: readonly ZoneSpan[] | null,
): VenueClock | null {
  const list = spans ? parse(spans) : table.get(zone);
  if (!list || list.length === 0) return null;
  const t = instant.getTime();
  const first = list[0];
  if (!first || t < first.from) return null;
  let hit = first;
  for (const s of list) {
    if (s.from <= t) hit = s;
    else break;
  }
  return { offset: hit.offset, abbrev: hit.abbrev };
}

/** The instant moved by an offset, so formatting it AS UTC reads the venue's clock. */
export function shifted(instant: Date, offset: number): Date {
  return new Date(instant.getTime() + offset * 60_000);
}

/**
 * A zone's offset at an instant according to the BROWSER's data, in minutes
 * east of UTC. For the device's zone, and to tell whether the browser's name
 * for a venue zone can be trusted.
 */
export function browserOffset(zone: string, instant: Date): number | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(instant);
    const n = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
    const wall = Date.UTC(n("year"), n("month") - 1, n("day"), n("hour"), n("minute"), n("second"));
    const whole = Math.floor(instant.getTime() / 1000) * 1000;
    return Math.round((wall - whole) / 60_000);
  } catch {
    return null;
  }
}

/** "GMT", "GMT+1", "GMT-3", "GMT+5:30" — Intl's own spelling of a fixed offset. */
export function gmtLabel(offset: number): string {
  if (offset === 0) return "GMT";
  const sign = offset > 0 ? "+" : "-";
  const abs = Math.abs(offset);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `GMT${sign}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
}
